import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { ProtectedRoute, PublicRoute, KycProtectedRoute, B2BProtectedRoute } from './ProtectedRoute';
import { useTranslation } from 'react-i18next';
import { ROUTES } from './routes.config';

// Lazy load layouts
const MainLayout = lazy(() => import('@/shared/components/layout/MainLayout'));
const AuthLayout = lazy(() => import('@/shared/components/layout/AuthLayout'));


// Lazy load pages - Auth
const WelcomePage = lazy(() => import('@/features/auth/pages/WelcomePage'));
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const JettRegisterPage = lazy(() => import('@/features/auth/pages/JettRegisterPage'));
const B2BInvitePage = lazy(() => import('@/features/auth/pages/B2BInvitePage'));
const RecoveryPasswordPage = lazy(() => import('@/features/auth/pages/RecoveryPasswordPage'));
const BusinessRegisterPage = lazy(() => import('@/features/auth/pages/BusinessRegisterPage'));
const AccountTypeSelectionPage = lazy(() => import('@/features/auth/pages/AccountTypeSelectionPage'));
const KycEducationPage = lazy(() => import('@/features/auth/pages/KycEducationPage'));
const ProcessBiometricPage = lazy(() => import('@/features/auth/pages/ProcessBiometricPage'));
const ValuePropositionPage = lazy(() => import('@/features/auth/pages/ValuePropositionPage'));

// Lazy load pages - Home (unified Send/Deliver)
const HomePage = lazy(() => import('@/features/home/pages/HomePage'));

// Lazy load pages - Sender
const PackagesListPage = lazy(() => import('@/features/packages/pages/PackagesListPage'));
const PackageNewPage = lazy(() => import('@/features/packages/pages/PackageNewPage'));
const PackageDetailPage = lazy(() => import('@/features/packages/pages/PackageDetailPage'));
const SenderIncentivesPage = lazy(() => import('@/features/sender/pages/SenderIncentivesPage'));
const NearbyJetpackersMapPage = lazy(
  () => import('@/features/sender/pages/NearbyJetpackersMapPage')
);

// Lazy load pages - Products (Sender's product management)
const ProductListPage = lazy(() => import('@/features/products/pages/ProductListPage'));
const ProductDetailPage = lazy(() => import('@/features/products/pages/ProductDetailPage'));
const RegisterProductPage = lazy(() => import('@/features/products/pages/RegisterProductPage'));
const ProductCheckoutSuccessPage = lazy(
  () => import('@/features/products/pages/ProductCheckoutSuccessPage')
);

// Lazy load pages - Jetpacker
const JetpackerDashboardPage = lazy(() => import('@/features/jetpacker/pages/JetpackerDashboardPage'));
const RequestsPage = lazy(() => import('@/features/jetpacker/pages/RequestsPage'));
const WithdrawPage = lazy(() => import('@/features/jetpacker/pages/WithdrawPage'));
const LevelsPage = lazy(() => import('@/features/jetpacker/pages/LevelsPage'));
const PerformancePage = lazy(() => import('@/features/jetpacker/pages/PerformancePage'));
const IncentivesPage = lazy(() => import('@/features/jetpacker/pages/IncentivesPage'));
const W9FormPage = lazy(() => import('@/features/jetpacker/pages/W9FormPage'));
const RoutesPage = lazy(() => import('@/features/jetpacker/pages/RoutesPage'));
const ChatAiPage = lazy(() => import('@/features/jetpacker/pages/ChatAiPage'));
const PerksPage = lazy(() => import('@/features/jetpacker/pages/PerksPage'));
const ItinerariesListPage = lazy(() => import('@/features/itineraries/pages/ItinerariesListPage'));
const ItineraryNewPage = lazy(() => import('@/features/itineraries/pages/ItineraryNewPage'));
const ItineraryDetailPage = lazy(() => import('@/features/itineraries/pages/ItineraryDetailPage'));
const ItineraryEditPage = lazy(() => import('@/features/itineraries/pages/ItineraryEditPage'));
const ItineraryConfirmationPage = lazy(() => import('@/features/itineraries/pages/ItineraryConfirmationPage'));
const DeliveriesListPage = lazy(() => import('@/features/deliveries/pages/DeliveriesListPage'));
const DeliveryProcessPage = lazy(() => import('@/features/deliveries/pages/DeliveryProcessPage'));
const CollectionProcessPage = lazy(() => import('@/features/deliveries/pages/CollectionProcessPage'));
const EarningsPage = lazy(() => import('@/features/deliveries/pages/EarningsPage'));
const BankAccountPage = lazy(() => import('@/features/deliveries/pages/BankAccountPage'));
const BadgesPage = lazy(() => import('@/features/deliveries/pages/BadgesPage'));

// Lazy load pages - Claims
const ClaimsListPage = lazy(() => import('@/features/claims/pages/ClaimsListPage'));
const NewClaimPage = lazy(() => import('@/features/claims/pages/NewClaimPage'));
const ClaimDetailPage = lazy(() => import('@/features/claims/pages/ClaimDetailPage'));

// Lazy load pages - Shared
const ChatListPage = lazy(() => import('@/features/chat/pages/ChatListPage'));
const ChatDetailPage = lazy(() => import('@/features/chat/pages/ChatDetailPage'));
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage'));
const SettingsPage = lazy(() => import('@/features/profile/pages/SettingsPage'));
const PaymentMethodsPage = lazy(() => import('@/features/profile/pages/PaymentMethodsPage'));
const NotificationCenterPage = lazy(() => import('@/features/shared/pages/NotificationCenterPage'));
const NotificationSettingsPage = lazy(() => import('@/features/shared/pages/NotificationSettingsPage'));
const PrivacySecurityPage = lazy(() => import('@/features/profile/pages/PrivacySecurityPage'));
const FaqPage = lazy(() => import('@/features/shared/pages/FaqPage'));
const ReferralsPage = lazy(() => import('@/features/shared/pages/ReferralsPage'));
const FeedbackPage = lazy(() => import('@/features/shared/pages/FeedbackPage'));
const RefundPolicyPage = lazy(() => import('@/features/shared/pages/RefundPolicyPage'));
const PricingTablePage = lazy(() => import('@/features/shared/pages/PricingTablePage'));
const TermsPage = lazy(() => import('@/features/shared/pages/TermsPage'));
const StripeReturnPage = lazy(() => import('@/features/shared/pages/StripeReturnPage'));
const ConnectReturnPage = lazy(() => import('@/features/shared/pages/ConnectReturnPage'));
const ConnectRefreshPage = lazy(() => import('@/features/shared/pages/ConnectRefreshPage'));
const EmpresasPage = lazy(() => import('@/features/shared/pages/EmpresasPage'));
const PublicShipmentFormPage = lazy(() => import('@/features/shared/pages/PublicShipmentFormPage'));
const TeLoLlevoPage = lazy(() => import('@/features/shared/pages/TeLoLlevoPage'));

// Lazy load pages - B2B
const B2BLayout = lazy(() => import('@/features/b2b/components/B2BLayout'));
const B2BDashboardPage = lazy(() => import('@/features/b2b/pages/B2BDashboardPage'));
const B2BPricingPage = lazy(() => import('@/features/b2b/pages/B2BPricingPage'));
const B2BPackagesPage = lazy(() => import('@/features/b2b/pages/B2BPackagesPage'));
const B2BPackageDetailPage = lazy(() => import('@/features/b2b/pages/B2BPackageDetailPage'));
const B2BBulkUploadPage = lazy(() => import('@/features/b2b/pages/B2BBulkUploadPage'));
const B2BPaymentMethodsPage = lazy(() => import('@/features/b2b/pages/B2BPaymentMethodsPage'));
const B2BAccountStatementPage = lazy(() => import('@/features/b2b/pages/B2BAccountStatementPage'));
const B2BReceiptPage = lazy(() => import('@/features/b2b/pages/B2BReceiptPage'));
const B2BReportsPage = lazy(() => import('@/features/b2b/pages/B2BReportsPage'));
const B2BCollectorsPage = lazy(() => import('@/features/b2b/pages/B2BCollectorsPage'));
const B2BTeamPage = lazy(() => import('@/features/b2b/pages/B2BTeamPage'));
const B2BProfilePage = lazy(() => import('@/features/b2b/pages/B2BProfilePage'));
const CollectorDashboardPage = lazy(() => import('@/features/b2b/pages/CollectorDashboardPage'));
const B2BTeLoLlevoPage = lazy(() => import('@/features/b2b/pages/B2BTeLoLlevoPage'));

// Lazy load pages - Rating redirect
const RateRedirectPage = lazy(() => import('@/features/products/pages/RateRedirectPage'));

// Lazy load pages - Tracking (Receiver view)
const TrackingDetailPage = lazy(() => import('@/features/products/pages/TrackingDetailPage'));
const TrackByCodePage = lazy(() => import('@/features/tracking/pages/TrackByCodePage'));
const PublicTrackingPage = lazy(() => import('@/features/tracking/pages/PublicTrackingPage'));

// Loading component for suspense sdasdasd
function PageLoader() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        <p className="text-gray-500 text-sm">{t('common.loading')}</p>
      </div>
    </div>
  );
}

// 404 Not Found page
function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-300">404</h1>
        <p className="mt-4 text-gray-600">{t('page404.message')}</p>
        <a href="/" className="mt-6 inline-block text-primary hover:underline">
          {t('page404.goBack')}
        </a>
      </div>
    </div>
  );
}

// Suspense wrapper for lazy loaded components
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// Router configuration
// eslint-disable-next-line react-refresh/only-export-components
export const router = createBrowserRouter([
  // Root redirect
  {
    path: '/',
    element: <Navigate to={ROUTES.AUTH.WELCOME} replace />,
  },

  // Public tracking routes (no auth required)
  {
    path: '/track',
    element: (
      <SuspenseWrapper>
        <TrackByCodePage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/track/:code',
    element: (
      <SuspenseWrapper>
        <PublicTrackingPage />
      </SuspenseWrapper>
    ),
  },

  // Auth routes
  {
    path: '/auth',
    element: (
      <SuspenseWrapper>
        <AuthLayout />
      </SuspenseWrapper>
    ),
    children: [
      {
        path: 'welcome',
        element: (
          <PublicRoute redirectAuthenticated>
            <SuspenseWrapper>
              <WelcomePage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: 'login',
        element: (
          <PublicRoute redirectAuthenticated>
            <SuspenseWrapper>
              <LoginPage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: 'register',
        element: (
          <PublicRoute redirectAuthenticated>
            <SuspenseWrapper>
              <RegisterPage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: 'jett-register',
        element: (
          <PublicRoute redirectAuthenticated>
            <SuspenseWrapper>
              <JettRegisterPage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: 'b2b-invite/:token',
        element: (
          <PublicRoute>
            <SuspenseWrapper>
              <B2BInvitePage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: 'recovery-password',
        element: (
          <PublicRoute>
            <SuspenseWrapper>
              <RecoveryPasswordPage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: 'business-register',
        element: (
          <PublicRoute redirectAuthenticated>
            <SuspenseWrapper>
              <BusinessRegisterPage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: 'account-type',
        element: (
          <PublicRoute redirectAuthenticated>
            <SuspenseWrapper>
              <AccountTypeSelectionPage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: 'kyc-education',
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <KycEducationPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'process-biometric',
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ProcessBiometricPage />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },

  // Unified Home route (Send/Deliver tabs)
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <HomePage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Shared about page (all authenticated users)
  {
    path: '/about',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <ValuePropositionPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Sender routes (packages, chat, profile, etc.)
  {
    path: '/sender',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'dashboard',
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'packages',
        element: (
          <SuspenseWrapper>
            <PackagesListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'packages/new',
        element: (
          <SuspenseWrapper>
            <PackageNewPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'packages/:id',
        element: (
          <SuspenseWrapper>
            <PackageDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'chat',
        element: (
          <SuspenseWrapper>
            <ChatListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'chat/:id',
        element: (
          <SuspenseWrapper>
            <ChatDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'profile',
        element: (
          <SuspenseWrapper>
            <ProfilePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings',
        element: (
          <SuspenseWrapper>
            <SettingsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'payment-methods',
        element: (
          <SuspenseWrapper>
            <PaymentMethodsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'referrals',
        element: (
          <SuspenseWrapper>
            <ReferralsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'incentives',
        element: (
          <SuspenseWrapper>
            <SenderIncentivesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'nearby-map',
        element: (
          <SuspenseWrapper>
            <NearbyJetpackersMapPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Jetpacker routes (KYC verification required)
  {
    path: '/jetpacker',
    element: (
      <KycProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </KycProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <SuspenseWrapper>
            <JetpackerDashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'requests',
        element: (
          <SuspenseWrapper>
            <RequestsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'itineraries',
        element: (
          <SuspenseWrapper>
            <ItinerariesListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'routes',
        element: (
          <SuspenseWrapper>
            <RoutesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'itineraries/new',
        element: (
          <SuspenseWrapper>
            <ItineraryNewPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'itineraries/confirmation',
        element: (
          <SuspenseWrapper>
            <ItineraryConfirmationPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'itineraries/:id',
        element: (
          <SuspenseWrapper>
            <ItineraryDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'itineraries/:id/edit',
        element: (
          <SuspenseWrapper>
            <ItineraryEditPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'deliveries',
        element: (
          <SuspenseWrapper>
            <DeliveriesListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'deliveries/:id',
        element: (
          <SuspenseWrapper>
            <DeliveryProcessPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'collection/:id',
        element: (
          <SuspenseWrapper>
            <CollectionProcessPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'chat',
        element: (
          <SuspenseWrapper>
            <ChatListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'chat/:id',
        element: (
          <SuspenseWrapper>
            <ChatDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'profile',
        element: (
          <SuspenseWrapper>
            <ProfilePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'earnings',
        element: (
          <SuspenseWrapper>
            <EarningsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'bank-account',
        element: (
          <SuspenseWrapper>
            <BankAccountPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'badges',
        element: (
          <SuspenseWrapper>
            <BadgesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'withdraw',
        element: (
          <SuspenseWrapper>
            <WithdrawPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'levels',
        element: (
          <SuspenseWrapper>
            <LevelsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'performance',
        element: (
          <SuspenseWrapper>
            <PerformancePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'incentives',
        element: (
          <SuspenseWrapper>
            <IncentivesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'referrals',
        element: (
          <SuspenseWrapper>
            <ReferralsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'w9-form',
        element: (
          <SuspenseWrapper>
            <W9FormPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'chat-ai',
        element: (
          <SuspenseWrapper>
            <ChatAiPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'perks',
        element: (
          <SuspenseWrapper>
            <PerksPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Tracking routes (Receiver's package tracking view)
  {
    path: '/tracking',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        path: ':id',
        element: (
          <SuspenseWrapper>
            <TrackingDetailPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Product routes (Sender's product management)
  {
    path: '/products',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <ProductListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'checkout-success',
        element: (
          <SuspenseWrapper>
            <ProductCheckoutSuccessPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: ':id',
        element: (
          <SuspenseWrapper>
            <ProductDetailPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Claims routes
  {
    path: '/claims',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <ClaimsListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'new',
        element: (
          <SuspenseWrapper>
            <NewClaimPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: ':id',
        element: (
          <SuspenseWrapper>
            <ClaimDetailPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Register Product route (outside MainLayout for full-screen wizard)
  {
    path: '/products/new',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <RegisterProductPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Shared routes (accessible by both roles)
  {
    path: '/notifications',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <NotificationCenterPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/faq',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <FaqPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/notifications-settings',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <NotificationSettingsPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/privacy-security',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <PrivacySecurityPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/feedback',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <FeedbackPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/refund-policy',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <RefundPolicyPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/pricing-table',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <PricingTablePage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/documents/:name',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <TermsPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/terms-conditions/:name',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <TermsPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/stripe-return',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <StripeReturnPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/connect-return',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <ConnectReturnPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/connect/refresh',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <ConnectRefreshPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // B2B operator routes (business accounts only)
  {
    path: '/b2b',
    element: (
      <B2BProtectedRoute>
        <SuspenseWrapper>
          <B2BLayout />
        </SuspenseWrapper>
      </B2BProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <SuspenseWrapper>
            <B2BDashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'pricing',
        element: (
          <SuspenseWrapper>
            <B2BPricingPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'packages',
        element: (
          <SuspenseWrapper>
            <B2BPackagesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'packages/:id',
        element: (
          <SuspenseWrapper>
            <B2BPackageDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'bulk-upload',
        element: (
          <SuspenseWrapper>
            <B2BBulkUploadPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports',
        element: (
          <SuspenseWrapper>
            <B2BReportsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'collectors',
        element: (
          <SuspenseWrapper>
            <B2BCollectorsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'team',
        element: (
          <SuspenseWrapper>
            <B2BTeamPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'profile',
        element: (
          <SuspenseWrapper>
            <B2BProfilePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'collector',
        element: (
          <SuspenseWrapper>
            <CollectorDashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'payment-methods',
        element: (
          <SuspenseWrapper>
            <B2BPaymentMethodsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'account-statement',
        element: (
          <SuspenseWrapper>
            <B2BAccountStatementPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'te-lo-llevo',
        element: (
          <SuspenseWrapper>
            <B2BTeLoLlevoPage />
          </SuspenseWrapper>
        ),
      },
      { index: true, element: <Navigate to="dashboard" replace /> },
    ],
  },

  // B2B receipt — standalone page (no sidebar), requires B2B auth
  {
    path: '/b2b/receipt/:id',
    element: (
      <B2BProtectedRoute>
        <SuspenseWrapper>
          <B2BReceiptPage />
        </SuspenseWrapper>
      </B2BProtectedRoute>
    ),
  },

  // Public rating page (accessed via email link with token, no login required)
  {
    path: '/rate-jetsender',
    element: (
      <SuspenseWrapper>
        <RateRedirectPage />
      </SuspenseWrapper>
    ),
  },

  // B2B landing page (public)
  {
    path: '/empresas',
    element: (
      <SuspenseWrapper>
        <EmpresasPage />
      </SuspenseWrapper>
    ),
  },

  // Te Lo Llevo (authenticated, sender + jetpacker)
  {
    path: '/te-lo-llevo',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <MainLayout />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <TeLoLlevoPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Public shipment quote form (no auth required)
  {
    path: '/quote_te-lo-llevo',
    element: (
      <SuspenseWrapper>
        <PublicShipmentFormPage />
      </SuspenseWrapper>
    ),
  },

  // 404 - Not found
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export { ROUTES } from './routes.config';
